/*
LIME LABS COMMERCE LAYER
Component: Predictive Search Viewport Controller
Project: La Troupe
Version: 1.4
Status: LLCL-QA

Geometry only. CSS owns presentation and the single-scroll contract.
*/
(() => {
  'use strict';

  const KEY = '__LLCL_PREDICTIVE_VIEWPORT_V14__';
  if (window[KEY]) return;
  window[KEY] = true;

  const PREVIEW_BAR_SELECTORS = [
    '#preview-bar-iframe',
    'iframe[id*="preview-bar"]',
    '.shopify-preview-bar',
    '[data-shopify-preview-bar]'
  ];

  let frame = 0;
  let resizeObserver = null;

  const getShells = () => Array.from(document.querySelectorAll('.header-search.llps-shell'));

  const getViewport = () => {
    const visual = window.visualViewport;

    if (visual) {
      return {
        top: visual.offsetTop,
        height: visual.height,
        bottom: visual.offsetTop + visual.height
      };
    }

    return {
      top: 0,
      height: window.innerHeight,
      bottom: window.innerHeight
    };
  };

  const getPreviewBarObstruction = (viewportBottom) => {
    let obstruction = 0;

    for (const selector of PREVIEW_BAR_SELECTORS) {
      for (const node of document.querySelectorAll(selector)) {
        if (!(node instanceof HTMLElement)) continue;

        const style = window.getComputedStyle(node);
        if (style.display === 'none' || style.visibility === 'hidden') continue;

        const rect = node.getBoundingClientRect();
        if (rect.width <= 0 || rect.height <= 0) continue;

        const touchesViewportBottom = rect.bottom >= viewportBottom - 4;
        if (!touchesViewportBottom) continue;

        obstruction = Math.max(obstruction, Math.min(rect.height, viewportBottom - rect.top));
      }
    }

    return Math.max(0, obstruction);
  };

  const setClass = (element, className, state) => {
    if (element.classList.contains(className) !== state) {
      element.classList.toggle(className, state);
    }
  };

  const measureShell = (shell) => {
    if (!(shell instanceof HTMLElement)) return;

    const rect = shell.getBoundingClientRect();
    if (rect.width <= 0) return;

    const hasResults = Boolean(shell.querySelector('.llps'));
    setClass(shell, 'llps-has-results', hasResults);

    if (!hasResults) {
      shell.style.removeProperty('--llps-surface-height');
      shell.style.removeProperty('--header-search-max-height');
      setClass(shell, 'llps-short-viewport', false);
      return;
    }

    const viewport = getViewport();
    const previewBarHeight = getPreviewBarObstruction(viewport.bottom);
    const safetyGap = window.matchMedia('(max-width: 749px)').matches ? 4 : 8;
    const shellTop = Math.max(rect.top, viewport.top);
    const availableHeight = Math.max(
      0,
      viewport.bottom - previewBarHeight - shellTop - safetyGap
    );

    if (!Number.isFinite(availableHeight) || availableHeight <= 0) return;

    const roundedHeight = Math.floor(availableHeight);

    /* Native Prestige reads --header-search-max-height in its shadow content.
       Feed it the same geometry as the LLCL surface so there is no outer scroll. */
    shell.style.setProperty('--llps-surface-height', `${roundedHeight}px`);
    shell.style.setProperty('--header-search-max-height', `${roundedHeight}px`);
    setClass(shell, 'llps-short-viewport', roundedHeight < 620);
  };

  const measure = () => {
    frame = 0;
    getShells().forEach(measureShell);
  };

  const schedule = () => {
    if (frame) return;
    frame = window.requestAnimationFrame(measure);
  };

  const bindResizeObserver = () => {
    if (!('ResizeObserver' in window)) return;

    resizeObserver?.disconnect();
    resizeObserver = new ResizeObserver(schedule);

    getShells().forEach((shell) => {
      const folio = shell.querySelector('.llps-shell__folio');
      const form = shell.querySelector('.llps-shell__form');
      if (folio instanceof HTMLElement) resizeObserver.observe(folio);
      if (form instanceof HTMLElement) resizeObserver.observe(form);
    });

    for (const selector of PREVIEW_BAR_SELECTORS) {
      document.querySelectorAll(selector).forEach((node) => {
        if (node instanceof HTMLElement) resizeObserver.observe(node);
      });
    }
  };

  const boot = () => {
    bindResizeObserver();
    schedule();
  };

  window.addEventListener('resize', schedule, { passive: true });
  window.addEventListener('orientationchange', schedule, { passive: true });

  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', schedule, { passive: true });
    window.visualViewport.addEventListener('scroll', schedule, { passive: true });
  }

  document.addEventListener('focusin', (event) => {
    if (event.target instanceof Element && event.target.closest('.header-search.llps-shell')) {
      schedule();
    }
  });

  document.addEventListener('input', (event) => {
    if (event.target instanceof Element && event.target.closest('.header-search.llps-shell')) {
      schedule();
    }
  }, true);

  document.addEventListener('toggle', schedule, true);
  document.addEventListener('shopify:section:load', boot);

  const mutationObserver = new MutationObserver((mutations) => {
    let relevant = false;
    let needsRebind = false;

    for (const mutation of mutations) {
      const target = mutation.target instanceof Element ? mutation.target : null;
      if (target?.closest('.header-search.llps-shell')) relevant = true;

      if (mutation.type === 'childList') {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof Element)) continue;

          if (
            node.matches('.header-search.llps-shell, .llps, #preview-bar-iframe') ||
            node.querySelector('.header-search.llps-shell, .llps, #preview-bar-iframe')
          ) {
            relevant = true;
            needsRebind = true;
          }
        }
      }
    }

    if (needsRebind) bindResizeObserver();
    if (relevant) schedule();
  });

  mutationObserver.observe(document.documentElement, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['open', 'aria-hidden', 'class']
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
